import * as RadixPopover from '@radix-ui/react-popover';
import cn from 'classnames';
import { FC, KeyboardEvent, ReactNode, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { Icon } from '@/shared/ui';

import {
  isSameTemplateLinkTarget,
  searchTemplateLinkOptions,
  type TemplateLineLinkEditing,
  type TemplateLinkOption,
  type TemplateLinkTarget,
} from '@/entities/templates';

import {
  getPopoverCollisionPadding,
  WINDOW_EDGE_GAP,
  type NamedNoteLinkOpener,
} from '../lib';

import styles from './LinkedLineMark.module.scss';

type Props = {
  // The options the mark names, each with its label: `listShownLineOpeners`.
  openers: NamedNoteLinkOpener[];
  dataComponent: string;
  // The template editor's: the mark opens a pop-up that changes the link.
  linkEditing?: TemplateLineLinkEditing;
  // Leads back to an option: the first one, or the one picked in the pop-up.
  onSelect: (opener: NamedNoteLinkOpener) => void;
  onMenuOpenChange?: (open: boolean) => void;
};

type LinkRowProps = {
  label: ReactNode;
  dataComponent: string;
  onSelect: () => void;
  onChange: () => void;
  onRemove: () => void;
};

// One end of a link in a pop-up of the template editor: it leads there,
// and has its own change by the pointer (✎) and removal (✕).
export const LinkRow: FC<LinkRowProps> = ({
  label,
  dataComponent,
  onSelect,
  onChange,
  onRemove,
}) => {
  const { formatMessage } = useIntl();
  const changeLabel = formatMessage({
    id: 'workspace.templateEdit.linkChange',
    defaultMessage: 'Change',
  });
  const removeLabel = formatMessage({
    id: 'workspace.templateEdit.linkRemove',
    defaultMessage: 'Remove link',
  });

  return (
    <span className={styles.openerRow} data-component={`${dataComponent}-row`}>
      <button
        type="button"
        className={cn('p3', styles.opener)}
        data-component={dataComponent}
        onClick={onSelect}
      >
        {label}
      </button>
      <button
        type="button"
        className={styles.openerTool}
        aria-label={changeLabel}
        title={changeLabel}
        data-component="workspace-template-link-change"
        onClick={onChange}
      >
        <Icon name="pen" size={14} />
      </button>
      <button
        type="button"
        className={cn(styles.openerTool, styles.openerRemove)}
        aria-label={removeLabel}
        title={removeLabel}
        data-component="workspace-template-link-remove"
        onClick={onRemove}
      >
        <Icon name="close" size={14} />
      </button>
    </span>
  );
};

type LinkMenuProps = {
  openers: NamedNoteLinkOpener[];
  linkEditing: TemplateLineLinkEditing;
  onClose: () => void;
  onSelect: (opener: NamedNoteLinkOpener) => void;
};

// What a linked line hangs on, each option with its own change by the
// pointer and removal, and a list of the options it may hang on, each
// checked while it does and switched by a click, the list staying open.
const LinkMenu: FC<LinkMenuProps> = ({
  openers,
  linkEditing,
  onClose,
  onSelect,
}) => {
  const { formatMessage } = useIntl();
  const [options] = useState(() => linkEditing.readOptions());
  const [query, setQuery] = useState('');
  const found = searchTemplateLinkOptions(options, query);
  const searchLabel = formatMessage({
    id: 'workspace.templateEdit.linkSearch',
    defaultMessage: 'Find an option',
  });

  const act = (action: () => void) => {
    onClose();
    action();
  };

  const isLinked = (option: TemplateLinkTarget) =>
    openers.some((opener) => isSameTemplateLinkTarget(opener, option));

  // The line's last option taken off unlinks it and its mark goes, so the
  // pop-up closes first, as its own ✕ does.
  const toggle = ({ blockId, value }: TemplateLinkOption) => {
    const target = { blockId, value };
    if (!isLinked(target)) {
      linkEditing.onAdd(target);
      return;
    }
    if (openers.length === 1) {
      onClose();
    }
    linkEditing.onRemove(target);
  };

  const nameOption = ({ label, sectionTitle }: TemplateLinkOption) =>
    sectionTitle
      ? formatMessage(
          {
            id: 'workspace.templateEdit.linkOptionInSection',
            defaultMessage: '{option} ({section})',
          },
          { option: label, section: sectionTitle },
        )
      : label;

  const toggleFirstFound = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter' && found[0]) {
      event.preventDefault();
      toggle(found[0]);
    }
  };

  return (
    <div className={styles.menu} data-component="workspace-template-link-menu">
      <span className={styles.part}>
        <span className={cn('p4', styles.title)}>
          <FormattedMessage
            id="workspace.templateEdit.linkShownWhen"
            defaultMessage="Shown when chosen"
          />
        </span>
        {openers.map((opener) => (
          <LinkRow
            key={`${opener.blockId}:${opener.value}`}
            label={opener.label}
            dataComponent="workspace-template-link-opener"
            onSelect={() => act(() => onSelect(opener))}
            onChange={() => act(() => linkEditing.onReplace(opener))}
            onRemove={() => act(() => linkEditing.onRemove(opener))}
          />
        ))}
      </span>
      <input
        className={cn('p3', styles.search)}
        type="search"
        value={query}
        placeholder={searchLabel}
        aria-label={searchLabel}
        autoFocus
        onChange={(event) => setQuery(event.target.value)}
        onKeyDown={toggleFirstFound}
      />
      {found.length > 0 ? (
        <ul className={styles.options}>
          {found.map((option) => (
            <li key={`${option.blockId}:${option.value}`}>
              <button
                type="button"
                className={cn('p3', styles.option)}
                aria-pressed={isLinked(option)}
                data-component="workspace-template-link-option"
                onClick={() => toggle(option)}
              >
                <span className={styles.check} aria-hidden>
                  {isLinked(option) && <Icon name="check" size={14} />}
                </span>
                {nameOption(option)}
              </button>
            </li>
          ))}
        </ul>
      ) : (
        <span className={cn('p3', styles.empty)}>
          <FormattedMessage
            id="workspace.templateEdit.linkNoOptions"
            defaultMessage="No options found"
          />
        </span>
      )}
    </div>
  );
};

// After a linked line's text, wherever a note or a template is read or
// edited: one compact "↳ option" naming the first option that opens the line
// and "+N" for the others, all of them with their lines in its tooltip. Like
// every mark of a link it takes no room until the line is hovered or holds
// the caret.
export const LinkedLineMark: FC<Props> = ({
  openers,
  dataComponent,
  linkEditing,
  onSelect,
  onMenuOpenChange,
}) => {
  const { formatMessage } = useIntl();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [first] = openers;
  if (!first) {
    return null;
  }

  const lineIds = [...new Set(openers.map((opener) => opener.lineId))];
  const description = lineIds
    .map((lineId) => {
      const ofLine = openers.filter((opener) => opener.lineId === lineId);
      const values = ofLine.map((opener) => opener.label);
      const line = ofLine[0].lineLabel;
      return values.length === 1
        ? formatMessage(
            {
              id: 'workspace.note.linkOpenedBy',
              defaultMessage: 'opened by “{value}”, line “{line}”',
            },
            { value: values[0], line },
          )
        : formatMessage(
            {
              id: 'workspace.note.linkOpenedByAny',
              defaultMessage: 'opened by any of: {values}; line “{line}”',
            },
            { values: values.join(', '), line },
          );
    })
    .join('; ');
  const text = (
    <>
      <span aria-hidden>↳</span>
      {first.label}
      {openers.length > 1 && (
        <span className={styles.more}>+{openers.length - 1}</span>
      )}
    </>
  );

  const changeMenuOpen = (open: boolean) => {
    setIsMenuOpen(open);
    onMenuOpenChange?.(open);
  };

  if (linkEditing) {
    return (
      <RadixPopover.Root open={isMenuOpen} onOpenChange={changeMenuOpen}>
        <RadixPopover.Trigger asChild>
          <button
            type="button"
            className={cn('p4', styles.mark)}
            title={description}
            aria-label={description}
            data-component={dataComponent}
            onMouseDown={(event) => event.preventDefault()}
          >
            {text}
          </button>
        </RadixPopover.Trigger>
        <RadixPopover.Portal>
          <RadixPopover.Content
            className={styles.popover}
            aria-label={description}
            side="bottom"
            align="start"
            sideOffset={6}
            collisionPadding={
              isMenuOpen ? getPopoverCollisionPadding() : WINDOW_EDGE_GAP
            }
            // Escape closes the pop-up only, never the modal it sits in.
            onEscapeKeyDown={(event) => event.stopPropagation()}
          >
            <LinkMenu
              openers={openers}
              linkEditing={linkEditing}
              onClose={() => changeMenuOpen(false)}
              onSelect={onSelect}
            />
          </RadixPopover.Content>
        </RadixPopover.Portal>
      </RadixPopover.Root>
    );
  }

  return (
    <button
      type="button"
      className={cn('p4', styles.mark)}
      title={description}
      aria-label={description}
      data-component={dataComponent}
      onMouseDown={(event) => event.preventDefault()}
      onClick={() => onSelect(first)}
    >
      {text}
    </button>
  );
};
